// Admin → Invoices: reads and the intent mutations, together because they
// share one gate — Full `billing_manage` plus an unscoped admin — and nothing
// else in the app may call them (the same shape `config-hooks.ts` uses).
//
// Every write is an INTENT. The API validates the target's state and
// queues a row; batch-worker does the Xero, R2 and email work within a
// minute, and the drawer polls the detail until the action settles.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, networkOnlyRead } from './client';
import { CADENCE } from './resource';
import { useRegisteredMutation } from './mutation';
import { adminInvoiceById, adminInvoices } from './resources';
import type { InvoicesQuery } from './resources';
import type {
  InvoiceActionEnqueued,
  InvoiceActionKind,
  InvoicePdfUrlResponse,
  InvoicePreviewResponse,
} from './types';

export function useInvoices(q: InvoicesQuery) {
  return useQuery(adminInvoices(q));
}

export function useInvoice(id: string | undefined) {
  return useQuery(adminInvoiceById(id ?? '', { enabled: !!id }));
}

/** Sidebar badge. An ambient consumer may poll slower than the open page. */
export function usePendingInvoiceCount(enabled: boolean) {
  return useQuery(adminInvoices({ state: 'pending_review' }, { enabled, cadence: CADENCE.lazy }));
}

/**
 * `retry: false`, and not as a preference: an approval is a state-changing
 * intent whose safety argument is that it is queued once against a state
 * someone reviewed. A 409 is exactly when the drawer must re-read, so the
 * invalidation runs on settled rather than on success.
 */
export function useInvoiceAction() {
  const queryClient = useQueryClient();
  return useRegisteredMutation({
    volatileResources: ['admin.invoices', 'admin.invoiceById'],
    gcTime: 0,
    retry: false,
    mutationFn: (input: { invoice_id: string; kind: InvoiceActionKind; expected_basis_fingerprint?: string }) =>
      api.post<InvoiceActionEnqueued>('/admin/invoices/actions', {
        body: {
          kind: input.kind,
          invoice_id: input.invoice_id,
          ...(input.expected_basis_fingerprint ? { expected_basis_fingerprint: input.expected_basis_fingerprint } : {}),
        },
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: adminInvoiceById.all });
    },
  });
}

/**
 * The dry run: what a period would be invoiced for right now. A read that
 * bypasses query state on purpose — the answer is for the confirmation
 * panel, not for a cache slot nothing else subscribes to.
 */
export function usePreviewInvoicePeriod() {
  return useRegisteredMutation({
    noDataEffect: true,
    gcTime: 0,
    mutationFn: (period: string) =>
      networkOnlyRead<InvoicePreviewResponse>(
        `/admin/invoices/preview?period=${encodeURIComponent(period)}`,
      ),
  });
}

export function useGenerateInvoicePeriod() {
  return useRegisteredMutation({
    volatileResources: ['admin.invoices'],
    gcTime: 0,
    retry: false,
    mutationFn: (period: string) =>
      api.post<InvoiceActionEnqueued>('/admin/invoices/actions', {
        body: { kind: 'generate_period', period },
      }),
  });
}

/** A presigned URL expires; it is an action's result, never cached data. */
export function useInvoicePdfUrl() {
  return useRegisteredMutation({
    noDataEffect: true,
    gcTime: 0,
    retry: false,
    mutationFn: (id: string) =>
      networkOnlyRead<InvoicePdfUrlResponse>(`/admin/invoices/${encodeURIComponent(id)}/pdf-url`),
  });
}
